import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContextMenu from '../Overlays/ContextMenu';
import InlineEditor from './InlineEditor';
import Modal from '../Overlays/Modal';
import SearchDialog from '../Overlays/SearchDialog';
import ModalMoveItems from '../Overlays/ModalMoveItems';
import { useExpandedFolders } from '../../state/useExpandedFolders';
import { openDoc, createDoc, duplicateDoc } from '../../helpers/docHelpers';
import { computeOpenBehavior } from '../../helpers/navHelpers';
import { renameProject, deleteItem, renameItem } from '../../helpers/projectHelpers';
import { createFolder } from '../../helpers/folderHelpers';
import { canRenameProject } from '../../helpers/projectAccess';
import { useViewMode } from '../../state/ViewModeContext';
import { debug } from '../../helpers/debug';
import { createVersion } from '../../helpers/versionHelpers';
import { apiLoadDoc } from '../../api/docs';
import { notifyToast } from '../../helpers/notificationHelpers';
import { docMapKey, folderMapKey, itemMapKey, findItem } from '../../helpers/mapKey';
import { screenSafePlacement } from '../../helpers/uiHelpers';
import { getPreference } from '../../helpers/preferencesHelpers';
import { getDefaultRenameMode, getRenameModePreferenceKey, isAutoRenameEligibleName, normalizeRenameMode } from '../../helpers/renameHelpers';
import Icon from '../Icon';

export default function ProjectPanel({
  user,
  project,
  projectReadOnly,
  tabs = [],
  setTabs = () => {},
  activeTabId = null,
  setActiveTabId = () => {},
  leftActiveTabId = null,
  rightActiveTabId = null,
  activePane = 'left',
  setActivePane = () => {},
  setLeftActiveTabId = () => {},
  setRightActiveTabId = () => {},
  setSplitScreen = () => {},
  inlineRenameRequest = null,
  editorRef = null,
  fixed = false,
  itemsArr = [],
  itemsMap = {},
  setItemsMap = () => {},
  refresh = () => {},
  dragStart = null,
  dragOver = null,
  dragEnd = null,
  dropOnItem = null,
  dropInProject = null,
  className = ''
}) {
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPlacement, setMenuPlacement] = useState('right');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [projectName, setProjectName] = useState(project?.name || 'Untitled Project');
  const [editingProject, setEditingProject] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
  const [moveModalState, setMoveModalState] = useState({ open: false, item: null });
  const [isolatedFolderId, setIsolatedFolderId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [docRenameMode, setDocRenameMode] = useState(() => {
    try {
      return normalizeRenameMode(getPreference(null, getRenameModePreferenceKey(null), getDefaultRenameMode(null)), null);
    } catch (err) {
      return getDefaultRenameMode(null);
    }
  });
  const navigate = useNavigate();

  const allowProjectRename = canRenameProject(project);
  const { isEditable, isPublic } = useViewMode();
  useEffect(() => {
    if (!allowProjectRename) {
      setEditingProject(false);
    }
  }, [allowProjectRename]);

  const menuRefs = useRef({});
  const { expandedFolders, setExpandedFolders } = useExpandedFolders(user);

  useEffect(() => {
    if (project?.name) setProjectName(project.name);
  }, [project]);

  useEffect(() => {
    if (!isolatedFolderId) return;
    const isolatedFolder = itemsMap.get(folderMapKey(isolatedFolderId));
    if (!isolatedFolder || isolatedFolder.type !== 'folder') {
      setIsolatedFolderId(null);
    }
  }, [isolatedFolderId, itemsMap]);

  useEffect(() => {
    const requestedDocId = inlineRenameRequest?.docId;
    if (!requestedDocId) return;

    const requestedName = inlineRenameRequest?.name || 'Untitled';
    setEditingId(docMapKey(requestedDocId));
    setEditName(requestedName);
  }, [inlineRenameRequest]);

  useEffect(() => {
    const handleRenameModeChanged = (event) => {
      if (String(event?.detail?.docType || '').trim().toLowerCase() === 'chat') return;
      setDocRenameMode(normalizeRenameMode(event?.detail?.mode, null));
    };

    window.addEventListener('rename-mode-changed', handleRenameModeChanged);
    return () => window.removeEventListener('rename-mode-changed', handleRenameModeChanged);
  }, []);

  const hasOpen = (itemId, itemType = null) => {
    const item = findItem(itemsMap, itemId, itemType);
    if (!item) return false;
    if (item.type === 'doc') {
      return tabs.some(t => t.docId === item.id);
    }
    return item.children?.some(child => hasOpen(child.id, child.type)) || false;
  };

  const hasActive = (itemId, itemType = null) => {
    const item = findItem(itemsMap, itemId, itemType);
    if (!item) return false;
    if (item.type === 'doc') {
      const openTab = tabs.find(t => t.docId === item.id);
      return openTab && (openTab.id === leftActiveTabId || openTab.id === rightActiveTabId);
    }
    return item.children?.some(child => hasActive(child.id, child.type)) || false;
  };

  const isFocused = (itemId, itemType = null) => {
    const item = findItem(itemsMap, itemId, itemType);
    if (!item) return false;
    if (item.type === 'doc') {
      const openTab = tabs.find(t => t.docId === item.id);
      const focusedTabId = activePane === 'left' ? leftActiveTabId : rightActiveTabId;
      return openTab?.id === focusedTabId;
    }
    return false;
  };

  const normalizeTabPane = pane => (pane === 'right' ? 'right' : 'left');
  const getPaneActiveDocId = (paneHint = activePane) => {
    const pane = normalizeTabPane(paneHint);
    const paneActiveTabId = pane === 'right' ? rightActiveTabId : leftActiveTabId;
    const paneActiveTab = tabs.find((tab) => String(tab?.id) === String(paneActiveTabId || ''));
    return paneActiveTab?.docId ?? null;
  };

  const notifyProjectPaneToast = (input, paneHint = activePane, options = {}) => {
    const baseWhere = options?.where && typeof options.where === 'object' ? options.where : {};
    const pane = normalizeTabPane(paneHint);
    const paneDocId = getPaneActiveDocId(pane);
    notifyToast(input, {
      ...options,
      where: {
        ...baseWhere,
        pane: baseWhere.pane || pane,
        docId: baseWhere.docId ?? paneDocId
      }
    });
  };

  const focusExistingTab = (tab) => {
    if (!tab) return;
    const targetPane = normalizeTabPane(tab.pane);
    if (targetPane === 'right') setRightActiveTabId?.(tab.id);
    else setLeftActiveTabId?.(tab.id);
    setActivePane?.(targetPane);
    setActiveTabId?.(tab.id);
  };

  const ensureTabPaneAssignment = (docId, targetPane) => {
    setTabs(prev =>
      prev.map(t =>
        t.docId === docId && (!t.pane || t.pane !== targetPane)
          ? { ...t, pane: targetPane }
          : t
      )
    );
  };

  const openDocFromProjectItems = (docItem, mode = null, preferredPane = null) => {
    if (!docItem) return;
    const existingTab = tabs.find(t => t.docId === docItem.id);

    if (existingTab) {
      focusExistingTab(existingTab);
      return;
    }

    // Determine open behavior from preferences when mode not explicitly provided
    let resolvedMode = mode;
    let resolvedPane = preferredPane || activePane;
    if (resolvedMode == null) {
      try {
        const beh = computeOpenBehavior({ type: docItem.docType === 'chat' ? 'chat' : 'doc', tabs, activePane, leftActiveTabId, rightActiveTabId });
        resolvedMode = beh.mode || 'replace';
        resolvedPane = beh.preferredPane || activePane;
      } catch (err) {
        resolvedMode = 'replace';
        resolvedPane = activePane;
      }
    }

    const targetPane = normalizeTabPane(resolvedPane || activePane);
    const paneActiveTabId = targetPane === 'right' ? rightActiveTabId : leftActiveTabId;
    const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
    const setFocusedTabId = (tabId) => {
      setPaneActiveTabId?.(tabId);
      setActiveTabId?.(tabId);
    };
    const paneTabs = targetPane === 'right'
      ? tabs.filter(t => t.pane === 'right')
      : tabs.filter(t => !t.pane || t.pane === 'left');
    const effectiveMode = resolvedMode === 'new' || paneTabs.length === 0 ? 'new' : resolvedMode;

    openDoc(docItem, tabs, setTabs, paneActiveTabId, setFocusedTabId, editorRef, effectiveMode);

    if (effectiveMode === 'new') {
      ensureTabPaneAssignment(docItem.id, targetPane);
    }

    setActivePane?.(targetPane);
  };

  const printDocFromMainApp = (docItem) => {
    if (!docItem?.id) return;
    try {
      const printUrl = `/d/${encodeURIComponent(String(docItem.id))}?print=1`;
      window.open(printUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      // no-op
    }
  };

  const toggleFolder = folderId => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  };

  const handleRenameProject = async () => {
    const trimmed = projectName.trim() || 'Untitled Project';
    setProjectName(trimmed);
    if (project?.id) await renameProject(project.id, trimmed);
    setEditingProject(false);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete || isDeleteConfirming) return;

    const target = pendingDelete;
    setIsDeleteConfirming(true);

    try {
      const deleted = await deleteItem(
        target,
        project?.id,
        user?.id,
        itemsMap,
        setItemsMap,
        tabs,
        setTabs,
        setActiveTabId
      );

      if (!deleted) {
        notifyProjectPaneToast({
          message: 'Unable to delete item. Please try again.',
          level: 'error'
        });
        return;
      }

      if (target.type === 'folder') {
        notifyProjectPaneToast('Folder deleted');
      } else {
        const deletedName = String(target?.name || '').trim();
        if (deletedName) {
          notifyProjectPaneToast({
            message: 'Deleted',
            suggestedName: deletedName
          });
        } else {
          notifyProjectPaneToast('Document deleted');
        }
      }
      setPendingDelete(null);
    } catch (err) {
      notifyProjectPaneToast({
        message: 'Unable to delete item. Please try again.',
        level: 'error',
        verboseMessage: err?.message || 'Delete failed',
        error: err,
        blocking: true
      });
    } finally {
      setIsDeleteConfirming(false);
    }
  };

  const handleOpenSearchResult = async (result) => {
    const resultDocId = String(result?.doc_id ?? '');
    if (!resultDocId) return;

    let foundDoc = null;
    for (const value of itemsMap.values()) {
      if (value?.type === 'doc' && String(value.id) === resultDocId) {
        foundDoc = value;
        break;
      }
    }

    let resolvedDocType = String(
      result?.doc_type ??
      result?.type ??
      foundDoc?.docType ??
      ''
    ).trim().toLowerCase() || null;

    if (!resolvedDocType) {
      try {
        const loadedDoc = await apiLoadDoc(resultDocId);
        resolvedDocType = String(loadedDoc?.type || '').trim().toLowerCase() || null;
      } catch (err) {
        resolvedDocType = null;
      }
    }

    if (foundDoc && resolvedDocType === 'chat' && foundDoc.docType !== 'chat') {
      foundDoc = {
        ...foundDoc,
        docType: 'chat',
        readOnly: true
      };
    }

    const docItem = foundDoc || {
      id: result?.doc_id,
      name: result?.doc_name || 'Untitled',
      type: 'doc',
      docType: resolvedDocType === 'chat' ? 'chat' : null,
      readOnly: resolvedDocType === 'chat'
    };

    openDocFromProjectItems(docItem);
  };

  const renderTree = (nodes = []) => {
    if (!Array.isArray(nodes)) return null;

    return nodes.map(item => {
      const latestItem = findItem(itemsMap, item.id, item.type);
      const resolvedItem = latestItem ? { ...item, ...latestItem } : item;
      const itemKey = itemMapKey(item);
      if (!menuRefs.current[itemKey]) {
        menuRefs.current[itemKey] = React.createRef();
      }

      const openTab = tabs.find(t => t.docId === resolvedItem.id);
      const isActiveTab = openTab && (openTab.id === leftActiveTabId || openTab.id === rightActiveTabId);
      const isOpenTab = !!openTab;
      const isEditing = editingId === itemKey;
      const showUntitledAutoStyle = resolvedItem.type === 'doc' && isAutoRenameEligibleName(resolvedItem?.name || '', resolvedItem?.docType || null, docRenameMode);

      const menuItemCount = resolvedItem.type === 'folder'
        ? (isEditable ? 8 : 1)
        : (isEditable ? 9 : 2);

      return (
        <li
          key={`${resolvedItem.type}-${resolvedItem.id}`}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
            dragOver?.(e, resolvedItem, id => setExpandedFolders(prev => new Set(prev).add(id)));
          }}
        >
          <div
            className={`projectItem ${resolvedItem.type === 'folder' ? 'folder' : ''} ${isActiveTab ? 'active' : ''} ${isOpenTab && !isActiveTab ? 'open' : ''} ${resolvedItem.type === 'folder' && hasOpen(resolvedItem.id, resolvedItem.type) ? 'has-open' : ''} ${resolvedItem.type === 'folder' && hasActive(resolvedItem.id, resolvedItem.type) ? 'has-active' : ''} ${isFocused(resolvedItem.id, resolvedItem.type) ? 'focused' : ''}`}
            id={`item-${resolvedItem.id}`}
            draggable={editingId !== itemKey}
            onDragStart={e => {
              if (editingId === itemKey) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              dragStart?.(e, resolvedItem.id, resolvedItem.type, {
                name: resolvedItem?.name || 'Untitled',
                docType: resolvedItem?.docType || null,
                readOnly: Boolean(resolvedItem?.readOnly) || resolvedItem?.docType === 'chat'
              });
            }}
            onDrop={async e => {
              e.preventDefault();
              e.stopPropagation();
              await dropOnItem?.(e);
            }}
            onDragEnd={dragEnd}
            onClick={() => {
              if (resolvedItem.type === 'folder') toggleFolder(resolvedItem.id);
              else openDocFromProjectItems(resolvedItem);
            }}
            onDoubleClick={() => {
              if (resolvedItem.type === 'folder') {
                setExpandedFolders(prev => new Set(prev).add(resolvedItem.id));
                setIsolatedFolderId(resolvedItem.id);
                return;
              }
              if (!isEditable) return;
              setEditingId(itemKey);
              setEditName(resolvedItem.name);
            }}
            data-open={resolvedItem.type === 'folder' ? (expandedFolders.has(resolvedItem.id) ? 'open' : 'closed') : undefined}
            data-tooltip={resolvedItem.name || 'Untitled'}
            data-tooltip-placement="bottom"
          >
            {resolvedItem.type === 'folder' ? (
              <Icon icon={expandedFolders.has(resolvedItem.id) ? 'folder-open' : 'folder'} className={`icon ${expandedFolders.has(resolvedItem.id) ? 'folder-open' : 'folder'}`} />
            ) : (
              <Icon icon={'doc'} className="icon doc" />
            )}

            {isEditing ? (
              <InlineEditor
                value={editName}
                onChange={setEditName}
                onSave={async () => {
                  const name = editName.trim();
                  if (!name) return;

                  await renameItem(resolvedItem.id, resolvedItem.type, name, itemsMap, setItemsMap, tabs, setTabs, project?.id, user?.id);

                    setEditingId(null);

                    // If a document was renamed, open and focus it in the editor
                    if (resolvedItem.type === 'doc') {
                      const updatedItem = itemsMap instanceof Map ? itemsMap.get(itemMapKey(resolvedItem)) : (itemsMap || {})[itemMapKey(resolvedItem)];
                      const docItem = updatedItem || { id: resolvedItem.id, name };
                      openDocFromProjectItems(docItem);
                    }
                }}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <span className={showUntitledAutoStyle ? 'untitledAutoName' : ''}>{resolvedItem.name || 'Untitled'}</span>
            )}

            <button
              ref={menuRefs.current[itemKey]}
              className="iconButton"
              data-tooltip-block-parent="true"
              onClick={e => {
                e.stopPropagation();
                if (menuOpenId === itemKey) {
                  setMenuOpenId(null);
                  return;
                }

                const nextPlacement = screenSafePlacement('right', menuRefs.current[itemKey]?.current, {
                  itemCount: menuItemCount
                });
                setMenuPlacement(nextPlacement);
                setMenuOpenId(itemKey);
              }}
              aria-label="Item actions"
            >
              <Icon icon={'dots-three-vertical'} className="icon dots-three-vertical" />
            </button>

            {menuOpenId === itemKey && (
              <ContextMenu
                triggerRef={menuRefs.current[itemKey]}
                onClose={() => setMenuOpenId(null)}
                placement={menuPlacement}
                items={
                  resolvedItem.type === 'folder'
                    ? [
                        isEditable && {
                          label: 'New Document',
                          iconClass: 'doc-add',
                          onClick: async () => {
                            setMenuOpenId(null);
                            if (!project?.id || !user?.id) return;

                            const newDoc = await createDoc(project.id, item.id, user.id, itemsMap, setItemsMap);
                            if (newDoc) {
                              // Ensure the parent folder is expanded so the inline editor is visible
                              setExpandedFolders(prev => {
                                const next = new Set(prev);
                                next.add(item.id);
                                return next;
                              });

                              const targetPane = normalizeTabPane(activePane);
                              const paneActiveTabId = targetPane === 'right' ? rightActiveTabId : leftActiveTabId;
                              const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
                              const setFocusedTabId = (tabId) => {
                                setPaneActiveTabId?.(tabId);
                                setActiveTabId?.(tabId);
                              };

                              openDoc(newDoc, tabs, setTabs, paneActiveTabId, setFocusedTabId, editorRef, 'new');
                              // Ensure the tab is assigned to the current editor pane and focus the pane
                              ensureTabPaneAssignment(newDoc.id, targetPane);
                              setActivePane?.(targetPane);

                              setEditingId(docMapKey(newDoc.id));
                              setEditName(newDoc.name);
                            }
                          }
                        },
                        isEditable && {
                          label: 'New Folder',
                          iconClass: 'folder-simple-plus',
                          onClick: async () => {
                            setMenuOpenId(null);
                            if (!project?.id || !user?.id) return;

                            const folder = await createFolder(project.id, 'New Folder', item.id, user?.id, itemsMap, setItemsMap);
                            if (folder) {
                              // Expand the parent so the new folder's inline editor is visible
                              setExpandedFolders(prev => {
                                const next = new Set(prev);
                                next.add(item.id);
                                return next;
                              });

                              setEditingId(folderMapKey(folder.id));
                              setEditName(folder.name);
                            }
                          }
                        },
                        {
                          label: 'Filter',
                          iconClass: 'funnel',
                          onClick: () => {
                            setMenuOpenId(null);
                            setExpandedFolders(prev => new Set(prev).add(item.id));
                            setIsolatedFolderId(item.id);
                          }
                        },
                        isEditable && {
                          label: 'Rename',
                          iconClass: 'pencil-simple',
                          onClick: () => {
                            setEditingId(itemKey);
                            setEditName(item.name);
                            setMenuOpenId(null);
                          }
                        },
                        isEditable && {
                          label: 'Move to project',
                          iconClass: 'box-arrow-up',
                          onClick: () => {
                            setMenuOpenId(null);
                            setMoveModalState({ open: true, item });
                          }
                        },
                        isEditable && { divider: true },
                        isEditable && {
                          label: 'Delete',
                          iconClass: 'trash',
                          onClick: () => {
                            setMenuOpenId(null);
                            setPendingDelete(item);
                          }
                        }
                      ].filter(Boolean)
                    : [
                        {
                          label: 'Open in new tab',
                          iconClass: 'arrow-square-out',
                          onClick: () => {
                            setMenuOpenId(null);
                            openDocFromProjectItems(item, 'new');
                          }
                        },
                        isEditable && {
                          label: 'Make a copy',
                          iconClass: 'copy',
                          onClick: async () => {
                            setMenuOpenId(null);
                            const copy = await duplicateDoc(item, project.id, user.id, itemsMap, setItemsMap);
                            if (copy) {
                              const sourceTab = tabs.find(t => t.docId === item.id && !t.isVersion);
                              const sourcePane = normalizeTabPane(sourceTab?.pane || activePane);
                              const targetPane = sourcePane === 'right' ? 'left' : 'right';

                              setSplitScreen?.(true);
                              openDocFromProjectItems(item, 'replace', sourcePane);
                              openDocFromProjectItems(copy, 'new', targetPane);
                              notifyProjectPaneToast('Copy created', targetPane, {
                                where: {
                                  docId: copy.id,
                                  pane: targetPane
                                }
                              });

                              setEditingId(docMapKey(copy.id));
                              setEditName(copy.name);
                            }
                          }
                        },
                        isEditable && {
                          label: 'Save Version',
                          iconClass: 'clock-counter-clockwise',
                          onClick: async () => {
                            setMenuOpenId(null);
                            try {
                              if (!project?.id) throw new Error('Missing project id');
                              const { loadDocContent } = await import('../../helpers/docHelpers');
                              const docContent = await loadDocContent(item.id);

                              const result = await createVersion({
                                projectId: project.id,
                                docId: item.id,
                                snapshot: {
                                  document: {
                                    project_id: project.id,
                                    doc_id: item.id,
                                    name: docContent?.name || item?.name || 'Untitled',
                                    layout: docContent?.layout || null,
                                    updated_at: docContent?.updated_at || new Date().toISOString(),
                                    content: docContent?.content ?? null
                                  },
                                  stats: docContent?.content_json?.stats || null
                                },
                                trigger: 'manual'
                              });

                              const versionId = result?.versionId || null;
                              const workerPath = versionId ? `/v1/docs/${item.id}/versions/${versionId}` : null;
                              const r2Path = versionId ? `docs/${item.id}/versions/${versionId}.json` : null;

                              debug('ProjectPanel.saveVersion', { type: 'audit', level: 1 }, {
                                projectId: project?.id,
                                docId: item.id,
                                versionId,
                                workerPath,
                                r2Path
                              });
                              const sourceTab = tabs.find(t => t.docId === item.id && !t.isVersion);
                              const toastPane = normalizeTabPane(sourceTab?.pane || activePane);
                              notifyProjectPaneToast('Version saved!', toastPane);
                            } catch (err) {
                              const message = err?.message || 'Failed to save version';
                              const sourceTab = tabs.find(t => t.docId === item.id && !t.isVersion);
                              const toastPane = normalizeTabPane(sourceTab?.pane || activePane);
                              if (message.includes('401') || message.toLowerCase().includes('auth')) {
                                notifyProjectPaneToast({
                                  message: 'Unable to save version. Please try again.',
                                  verboseMessage: 'Version save failed due to worker/database authentication configuration.',
                                  error: err,
                                  blocking: true,
                                  level: 'error'
                                }, toastPane);
                              } else {
                                notifyProjectPaneToast({
                                  message: 'Unable to save version. Please try again.',
                                  verboseMessage: message,
                                  error: err,
                                  blocking: true,
                                  level: 'error'
                                }, toastPane);
                              }
                              // eslint-disable-next-line no-console
                              console.error('Save Version error', message);
                            }
                          }
                        },
                        isEditable && {
                          label: 'Rename',
                          iconClass: 'pencil-simple',
                          onClick: () => {
                            setEditingId(itemKey);
                            setEditName(item.name);
                            setMenuOpenId(null);
                          }
                        },
                        {
                          label: 'Print',
                          iconClass: 'printer',
                          onClick: () => {
                            setMenuOpenId(null);
                            printDocFromMainApp(item);
                          }
                        },
                        isEditable && {
                          label: 'Move to project',
                          iconClass: 'box-arrow-up',
                          onClick: () => {
                            setMenuOpenId(null);
                            setMoveModalState({ open: true, item });
                          }
                        },
                        isEditable && { divider: true },
                        isEditable && {
                          label: 'Delete',
                          iconClass: 'trash',
                          onClick: () => {
                            setMenuOpenId(null);
                            setPendingDelete(item);
                          }
                        }
                      ].filter(Boolean)
                }
              />
            )}

          </div>

          {item.children?.length > 0 && expandedFolders.has(item.id) && (
            <ul className="subList">{renderTree(item.children)}</ul>
          )}
        </li>
      );
    });
  };

  const findNodeById = (nodes = [], id, type = null) => {
    for (const node of nodes) {
      if (node.id === id && (type ? node.type === type : true)) return node;
      if (node.children?.length) {
        const found = findNodeById(node.children, id, type);
        if (found) return found;
      }
    }
    return null;
  };

  const isolatedFolder = isolatedFolderId ? findNodeById(itemsArr, isolatedFolderId, 'folder') : null;
  const isIsolatedView = Boolean(isolatedFolder && isolatedFolder.type === 'folder');
  const treeNodes = isIsolatedView
    ? [isolatedFolder]
    : itemsArr;

  return (
    <div
      id="projectPanel"
      className={`${fixed ? 'fixed' : 'overlay'} ${className || ''}`}
      onDragOver={e => isEditable && e.preventDefault()}
      onDrop={isEditable ? dropInProject : undefined}
    >
      <div id="projectHeader">
        <div
          className="projectName"
          onDoubleClick={allowProjectRename ? () => setEditingProject(true) : undefined}
        >
          {!isPublic && (
            <button
              type="button"
              className="iconButton"
              aria-label="Back to projects"
              onClick={() => navigate('/projects')}
            >
              <Icon icon={'caret-left'} className="icon caret-left" />
            </button>
          )}
          {editingProject && allowProjectRename ? (
            <InlineEditor value={projectName} onChange={setProjectName} onSave={handleRenameProject} onCancel={() => setEditingProject(false)} />
          ) : (
            <h3>{projectName}</h3>
          )}
        </div>

        <div className="projectActions">
          <button
            className="iconTextButton list"
            onClick={() => {
              setSearchOpen(true);
            }}
          >
            <Icon icon={'magnifying-glass'} className="icon magnifying-glass" />
            <span>Search</span>
          </button>

          {isEditable && (
            <button
              className="iconTextButton list"
              onClick={async () => {
                if (!project?.id || !user?.id) return;
                const targetFolderId = isIsolatedView ? isolatedFolder.id : null;

                const newDoc = await createDoc(project.id, targetFolderId, user.id, itemsMap, setItemsMap);

                if (newDoc) {
                  const targetPane = normalizeTabPane(activePane);
                  const paneActiveTabId = targetPane === 'right' ? rightActiveTabId : leftActiveTabId;
                  const setPaneActiveTabId = targetPane === 'right' ? setRightActiveTabId : setLeftActiveTabId;
                  const setFocusedTabId = (tabId) => {
                    setPaneActiveTabId?.(tabId);
                    setActiveTabId?.(tabId);
                  };

                  openDoc(newDoc, tabs, setTabs, paneActiveTabId, setFocusedTabId, editorRef, 'new');
                  ensureTabPaneAssignment(newDoc.id, targetPane);
                  setActivePane?.(targetPane);

                  setEditingId(docMapKey(newDoc.id));
                  setEditName(newDoc.name);
                }
              }}
            >
              <Icon icon={'doc-add'} className="icon doc-add" />
                <span>New Document</span>
            </button>
          )}

          {isEditable && (
            <button
              className="iconTextButton list"
              onClick={async () => {
                if (!project?.id) return;
                const targetFolderId = isIsolatedView ? isolatedFolder.id : null;

                const folder = await createFolder(project.id, 'New Folder', targetFolderId, user?.id, itemsMap, setItemsMap);

                if (folder) {
                  setEditingId(folderMapKey(folder.id));
                  setEditName(folder.name);
                }
              }}
            >
                <Icon icon={'folder-simple-plus'} className="icon folder-simple-plus" />
                <span>New Folder</span>
            </button>
          )}
        </div>
      </div>
      <ModalMoveItems
        isOpen={Boolean(moveModalState.open)}
        onCancel={() => setMoveModalState({ open: false, item: null })}
        item={moveModalState.item}
        user={user}
        srcProjectId={project?.id}
        itemsMap={itemsMap}
        setItemsMap={setItemsMap}
        tabs={tabs}
        setTabs={setTabs}
        setActiveTabId={setActiveTabId}
        refresh={refresh}
      />

      <div id="projectContent">
        <div className="projectListHeader">
          {isIsolatedView ? (
            <div
              className="projectFilterChip"
              onClick={() => setIsolatedFolderId(null)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsolatedFolderId(null);
                }
              }}
              aria-label="Clear folder filter"
              role="button"
              tabIndex={0}
            >
              <span>{`Filter: ${isolatedFolder?.name || 'Folder'}`}</span>
              <Icon icon={'x'} className="icon x" />
            </div>
          ) : (
            <span className="projectListTitle">All items</span>
          )}
        </div>
        <ul className="projectList" onDragOver={e => isEditable && e.preventDefault()} onDrop={isEditable ? dropInProject : undefined}>
          {renderTree(treeNodes)}
        </ul>
      </div>

      <Modal
        isOpen={!!pendingDelete}
        title="Confirm Delete"
        message={
          pendingDelete
            ? pendingDelete.type === 'folder'
              ? `Are you sure you want to delete the folder "${pendingDelete.name}" and everything inside it?`
              : `Are you sure you want to delete "${pendingDelete.name}"?`
            : ''
        }
        confirmText={isDeleteConfirming ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (isDeleteConfirming) return;
          setPendingDelete(null);
        }}
        confirmDisabled={isDeleteConfirming}
        cancelDisabled={isDeleteConfirming}
      />

      <SearchDialog
        isOpen={searchOpen}
        onClose={() => setSearchOpen(false)}
        projectId={project?.id}
        onSelectResult={handleOpenSearchResult}
      />
    </div>
  );
}

